import { DASH, formatClock, formatNumber, formatSigned, humanize } from "../format";
import type { EmotionResult, EmotionSide } from "../types";
import { Block, Distribution, Empty, Unavailable } from "./shared";

// What each source can detect (RESULTS.md). Audio has no frustration or confusion class,
// so its numbers must never be read as covering them.
const TEXT_LABELS = "frustration, excitement, sadness, confusion, calmness, anger, happiness, neutral";
const AUDIO_LABELS = "anger, disgust, fear, happiness, neutral, sadness, surprise";

function SideCard({
  title,
  detects,
  side,
  missingReason,
}: {
  title: string;
  detects: string;
  side: EmotionSide | null;
  missingReason: string;
}) {
  return (
    <section className="space-y-3 rounded-xl border border-line bg-canvas p-3" aria-label={title}>
      <div>
        <h4 className="text-sm font-semibold text-ink">{title}</h4>
        <p className="text-xs text-ink-3">Detects: {detects}.</p>
      </div>
      {side ? (
        <>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <div><dt className="text-xs text-ink-3">Whole call</dt><dd className="font-medium text-ink">{humanize(side.dominant)}</dd></div>
            <div><dt className="text-xs text-ink-3">Customer</dt><dd className="font-medium text-ink">{humanize(side.customer_dominant)}</dd></div>
          </dl>
          <Distribution values={side.distribution} label={`${title} distribution`} />
          <p className="text-[11px] text-ink-3">Model: {side.model || DASH}{side.prompt_version ? ` · ${side.prompt_version}` : ""}</p>
        </>
      ) : (
        <Unavailable>Did not run: {missingReason}</Unavailable>
      )}
    </section>
  );
}

export function EmotionPanel({ result }: { result: EmotionResult }) {
  const av = result.availability;
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-2">
        <SideCard
          title="From the words (text)"
          detects={TEXT_LABELS}
          side={av.text ? result.text : null}
          missingReason={av.text_reason || "no transcript to read."}
        />
        <SideCard
          title="From the voice (audio)"
          detects={AUDIO_LABELS}
          side={av.audio ? result.audio : null}
          missingReason={av.audio_reason || "no usable recording."}
        />
      </div>
      {!av.timing ? <Unavailable>The call could not be aligned to its recording, so turns have no times.</Unavailable> : null}

      <Block title="Per turn">
        {result.turns.length ? (
          <div className="overflow-x-auto rounded-xl border border-line">
            <table className="w-full text-sm">
              <thead className="bg-soft text-left text-xs text-ink-3">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">Turn</th>
                  <th scope="col" className="px-3 py-2 font-medium">Text</th>
                  <th scope="col" className="px-3 py-2 font-medium">Audio</th>
                  <th scope="col" className="px-3 py-2 font-medium">Pace</th>
                </tr>
              </thead>
              <tbody>
                {result.turns.map((turn) => (
                  <tr key={turn.turn_index} className="border-t border-line align-top">
                    <td className="px-3 py-1.5 text-ink-2">
                      {turn.turn_index + 1} · {turn.speaker === "contact" ? "Customer" : "Agent"}
                      <div className="text-xs text-ink-3">{formatClock(turn.start)}</div>
                    </td>
                    <td className="px-3 py-1.5 text-ink">
                      {turn.text ? humanize(turn.text.primary) : DASH}
                      {turn.text ? <div className="text-xs text-ink-3">confidence {formatNumber(turn.text.confidence, 2)}</div> : null}
                    </td>
                    <td className="px-3 py-1.5 text-ink">
                      {turn.audio ? humanize(turn.audio.primary) : DASH}
                      {turn.audio ? (
                        <div className="text-xs text-ink-3">valence {formatSigned(turn.audio.valence)} · arousal {formatNumber(turn.audio.arousal, 2)}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums text-ink-2">
                      {turn.speaking_pattern ? `${formatNumber(turn.speaking_pattern.words_per_minute)} wpm` : DASH}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty>No turns were scored.</Empty>}
      </Block>
    </div>
  );
}
